import React, { useState } from "react";
import {
  Alert,
  Typography,
  Stack,
  TextField,
  Button,
  Chip,
  Paper,
  Box,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import SendIcon from "@mui/icons-material/Send";

interface MeetingSubstituteInviteFormProps {
  submitEmails: (emails: string[]) => void;
  domainWhiteList: string[];
}

const MeetingSubstituteInviteForm: React.FC<MeetingSubstituteInviteFormProps> = ({ submitEmails, domainWhiteList }) => {

  const [emailList, setEmailList] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");

  const emailLimitReached = emailList.length >= 5;

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const addEmail = () => {
    if (emailLimitReached) {
      return;
    }

    const email = emailInput.trim().toLowerCase();

    if (!email) {
      return;
    }

    if (emailList.includes(email)) {
      setEmailError("Email already added");
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError("Enter a valid email address");
      return;
    }

    if (domainWhiteList.length > 0) {
      const emailDomain = email.split("@")[1];
      if (!domainWhiteList.includes(emailDomain)) {
        setEmailError(`Email domain must be one of: ${domainWhiteList.join(", ")}`);
        return;
      }
    }

    setEmailList((prev) => [...prev, email]);
    setEmailInput("");
    setEmailError("");
  };

  const removeEmail = (email: string) => {
    setEmailList((prev) => prev.filter((e) => e !== email));
  };

  const confirmEmailList = () => {
    if (emailList.length === 0) {
      setEmailError("Add at least one email");
      return;
    }

    submitEmails(emailList);
  };

  return (
    <Stack spacing={2} width="100%">
      {domainWhiteList.length > 0 && (
        <Stack spacing={1} width="100%">
          <Alert severity="info">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2">
                Allowed email domains:
              </Typography>
              {domainWhiteList.map((domain) => (
                <Box
                  key={domain}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    px: 1,
                    backgroundColor: "background.paper",
                  }}
                >
                  <Typography variant="body2" fontFamily="monospace">{domain}</Typography>
                </Box>
              ))}
            </Stack>
          </Alert>
        </Stack>
      )}
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          label="Add substitute email"
          placeholder={domainWhiteList.length > 0 ? `name@${domainWhiteList[0]}` : "name@example.com"}
          value={emailInput}
          error={!!emailError}
          disabled={emailLimitReached}
          onChange={(e) => {
            setEmailInput(e.target.value);
            if (emailError) setEmailError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addEmail();
            }
          }}
        />
        <Button
          variant="contained"
          startIcon={<Add />}
          disabled={emailLimitReached || !isValidEmail(emailInput.trim())}
          onClick={addEmail}
          sx={{ borderRadius: 1 }}
        >
          Add
        </Button>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          minHeight: 64,
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          borderStyle: "dashed",
        }}
      >
        {emailList.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No emails added yet
          </Typography>
        ) : (
          emailList.map((email) => (
            <Chip
              key={email}
              label={email}
              onDelete={() => removeEmail(email)}
              color="primary"
              variant="outlined"
            />
          ))
        )}
      </Paper>

      {emailError && (
        <Alert severity="error">{emailError}</Alert>
      )}

      <Button
        variant="contained"
        startIcon={<SendIcon />}
        onClick={confirmEmailList}
        disabled={emailList.length === 0}
      >
        Invite
      </Button>
    </Stack>
  );
};

export default MeetingSubstituteInviteForm;
